/**
 * Utility functions for UK tax year calculations
 */

/**
 * Get the current UK tax year
 * @returns {number} The start year of the current tax year
 */
export const getCurrentTaxYear = () => {
  const now = new Date();
  // UK tax year starts on April 6th
  // If we're on April 5th or earlier, we're in the previous tax year
  return now.getMonth() < 3 || (now.getMonth() === 3 && now.getDate() < 6) 
    ? now.getFullYear() - 1 
    : now.getFullYear();
};

/**
 * Get tax year date boundaries based on the start year
 * @param {number} startYear - The start year of the tax year
 * @returns {Object} Object containing start and end dates, and a formatted label
 */
export const getTaxYearBoundaries = (startYear) => {
  const startYearNum = typeof startYear === 'string' ? parseInt(startYear, 10) : startYear;
  const endYear = startYearNum + 1;
  
  // Tax year runs from April 6th to April 5th the next year
  const start = new Date(startYearNum, 3, 6); // April 6th of start year (month is 0-indexed)
  const end = new Date(endYear, 3, 5, 23, 59, 59, 999); // April 5th of end year
  
  return {
    start,
    end,
    label: `${startYearNum}-${endYear}`
  };
};

/**
 * Check if a date falls within a specific tax year
 * @param {Date} date - The date to check
 * @param {number} taxYearStart - The start year of the tax year
 * @returns {boolean} True if the date is within the tax year
 */
export const isDateInTaxYear = (date, taxYearStart) => {
  const { start, end } = getTaxYearBoundaries(taxYearStart);
  return date >= start && date <= end;
};

/**
 * Get an array of available tax years from the current year back to a specified limit
 * @param {number} yearsBack - Number of previous tax years to include
 * @returns {Array} Array of tax year start years
 */
export const getAvailableTaxYears = (yearsBack = 2) => { // Default to only 2 years back
  const currentTaxYear = getCurrentTaxYear();
  const years = [currentTaxYear];
  
  for (let i = 1; i <= yearsBack; i++) {
    years.push(currentTaxYear - i);
  }
  
  return years;
};

/**
 * Format a tax year as a string
 * @param {number} startYear - The start year of the tax year
 * @returns {string} Formatted tax year string (e.g. "2024-2025")
 */
export const formatTaxYear = (startYear) => {
  const startYearNum = typeof startYear === 'string' ? parseInt(startYear, 10) : startYear;
  return `${startYearNum}-${startYearNum + 1}`;
};
